import { landmineNames } from '@mm/constants/landmine-names';
import { useClickOutside } from '@mm/hooks';
import { ArrowStatefulIcon } from '@mm/icons';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import commonHandler from '@mm/state/errorHandlers/common-handler';
import { useFetchAudiences } from '@mm/state/hooks/use-fetch-audiences';
import './AudienceSelector.pcss';
import AudiencesModel, { TagFilterItem } from '@mm/state/model/AudiencesModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import { partial, unionBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import ProRibbon from '../../../../../common/components/ProRibbon/ProRibbon';
import {
  LeadContactsFilterByAudience,
  LeadContactsFilterByAudienceCreateButton,
} from '../../../beta/modules/Audiences/LeadContactsFilterByAudience/LeadContactsFilterByAudience';
import { useCreateNewAudience } from '../../../beta/modules/Audiences/use-create-new-audience';
import { useCreateFilterParams } from '../../../beta/modules/Contacts/use-create-filter-params';
import {
  prepareEsFiltersStructure,
  unassignFilterParams,
} from '../../../beta/modules/Contacts/MyLeadsFiltersPanel/helpers';

type Props = {
  selectedAudienceId?: number;
  fallbackId?: number; // audience id to select if couldn't fetch
  selectedAudience?: AudiencesModel;
  onAudienceSelected: (audienceId: number) => void;
  disabled?: boolean;
  parentAudiences?: AudiencesModel[];
  formatText?: (name?: string) => string;
  autoselect?: boolean;
  className?: string;
  onCancel?: () => void;
  emphasizeAllContactsForRestore?: boolean;
};

export const AudienceSelector = (props: Props) => {
  const {
    fallbackId = -1,
    selectedAudienceId,
    onAudienceSelected,
    disabled = false,
    parentAudiences,
    formatText,
    autoselect = true,
    className,
    onCancel,
    emphasizeAllContactsForRestore = false,
  } = props;

  const audiences = useSelector(selectAudienceSorted);
  let mergedAudiences = unionBy(parentAudiences, audiences, 'id');
  const allContactsAudience = mergedAudiences.find(
    a => a.name === 'All contacts',
  );

  if (emphasizeAllContactsForRestore && allContactsAudience) {
    mergedAudiences = [
      allContactsAudience,
      ...mergedAudiences.filter(a => a.id !== allContactsAudience.id),
    ];
  }

  const modalCRUDActions = useModalActions();

  const { fetchAllContacts, fetch, fetchMore, isFetching } = useFetchAudiences({
    skipBusy: true,
    fetchAllContactsOnInit: emphasizeAllContactsForRestore,
  });

  const canSelectAudiences = useSelector(
    combinedFeatureFlagsSelector,
  ).campaignsAudiencesSelection;

  useEffect(() => {
    if (autoselect && !selectedAudienceId && mergedAudiences[0]) {
      onAudienceSelected(mergedAudiences[0].id);
    }
  }, [JSON.stringify(mergedAudiences)]);

  const upgradeGuardPass = (audienceId: number) => {
    const selectedAudience = mergedAudiences.find(
      item => item.id === audienceId,
    );
    if (!canSelectAudiences && selectedAudience?.messengerAdAudience) {
      modalCRUDActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['SELECT-AUDIENCE'],
        feature: 'campaignsAudiencesSelection',
      });
      return false;
    }

    return true;
  };

  const { createOrEditAudienceAndOpenMenu } = useCreateNewAudience();

  const selectedAudience = mergedAudiences.find(
    a => a.id === selectedAudienceId,
  );

  const { filterParams, setFilterParams } = useCreateFilterParams({
    query: prepareEsFiltersStructure(selectedAudience?.esFilters) || {},
    filterParams: unassignFilterParams(selectedAudience?.filterParams),
    tagsFilters: unassignFilterParams(
      selectedAudience?.tagsFilters,
    ) as TagFilterItem[],
    page: 1,
  });

  const [isOpen, setIsOpen] = useState<boolean>();

  const onClose = partial(setIsOpen, false);
  const toggle = async () => {
    if (!isOpen) {
      await reloadAudienceData();
    }
    setIsOpen(current => !current);
  };

  const onSelect = (audienceId?: number) => {
    if (audienceId && upgradeGuardPass(audienceId)) {
      onClose();
      onAudienceSelected(audienceId);
    }
  };

  const ref = useClickOutside<HTMLDivElement>(onClose);

  const reloadAudienceData = async () => {
    // We await here since "fetch" clears existing audiences from redux
    await fetch(1, 25);

    if (emphasizeAllContactsForRestore) {
      await fetchAllContacts();
    }
  };

  const audiencesCRUDActions = useAudiencesCRUDActions();
  const botId = useSelector(activeChatbotIdSelector);
  const dispatch: Dispatch<StoreState> = useDispatch();

  useEffect(() => {
    if (
      selectedAudienceId &&
      !mergedAudiences.find(audience => audience.id === selectedAudienceId)
    ) {
      audiencesCRUDActions
        .retrieveAudienceById(botId, selectedAudienceId, true)
        .catch(err => {
          if (err.status === 404) {
            onAudienceSelected(fallbackId);
          } else {
            commonHandler(dispatch)(err);
          }
        });
    }
  }, []);

  const formattedText = useMemo(() => {
    if (formatText) {
      return formatText(selectedAudience?.name);
    }
    if (
      emphasizeAllContactsForRestore &&
      allContactsAudience &&
      selectedAudience &&
      selectedAudience.id === allContactsAudience.id
    ) {
      return 'Selected: All website visitor data';
    }

    if (selectedAudience?.name) {
      return `Selected: ${selectedAudience?.name}`;
    }

    return 'Select audience';
  }, [selectedAudience?.name, formatText]);

  return (
    <div className={classNames('audience-selector', className)} ref={ref}>
      <div className="audience-selector__input-container" onClick={toggle}>
        {formattedText}
        <ArrowStatefulIcon
          className="audience-selector__arrow"
          direction={isOpen ? 'up' : 'down'}
          size={14}
        />
      </div>
      {isOpen && (
        <LeadContactsFilterByAudience
          audiences={mergedAudiences}
          emphasizeAllContactsForRestore={emphasizeAllContactsForRestore}
          withMenu="edit"
          className="audience-selector__dropdown"
          listClassName="audience-selector__dropdown-list"
          isFetching={isFetching}
          onScrollEnd={isFetching ? undefined : fetchMore}
          activeAudienceId={selectedAudienceId}
          onSelect={onSelect}
          renderAudienceChild={audience => {
            if (!audience.messengerAdAudience) {
              return null;
            }

            return (
              <ProRibbon
                className="audience-selector__pro-audience"
                ribbonType="unicorn"
              />
            );
          }}
          headingSlotChildren={
            <LeadContactsFilterByAudienceCreateButton
              onClick={async () => {
                const id = await createOrEditAudienceAndOpenMenu({
                  activeAudience: selectedAudience,
                  filterParams,
                  setFilterParams,
                  title: 'Build Audience',
                  buttonName: 'Save',
                  withSaveAsNew: false,
                  hiddenAudience: true,
                  onSave: onClose,
                });
                if (id) {
                  onSelect(id);
                }
              }}
            />
          }
        />
      )}
    </div>
  );
};
